import {Component, Input, OnChanges, QueryList, SimpleChanges, ViewChild, ViewChildren, Output, EventEmitter} from '@angular/core';
import {ApplicantModel, KeyIdentifierModel} from '@service/models';
import {NgForm} from '@angular/forms';
import {AppConfigService} from '@service/config/app-config.service';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import * as moment from 'moment/moment';
import {DateComponent} from '@shared/form-components/date/date.component';
import {SsnComponent} from '@shared/form-components/ssn/ssn.component';
import {AddressComponent} from '@shared/form-components/address/address.component';
import {PhoneNumberComponent} from '@shared/form-components/phone-number/phone-number.component';
import {getInstitutionId} from '../../../constants/constant';
import {environment} from '@env/environment';
import { ElementRef } from '@angular/core';
@Component({
    selector: 'cs-beneficiary',
    templateUrl: './beneficiary.component.html',
    styleUrls: ['./beneficiary.component.scss']
})
export class BeneficiaryComponent implements OnChanges {
    @ViewChild('beneficiaryForm') _beneficiaryForm: NgForm;
    @ViewChildren('dob') dob: QueryList<DateComponent>;
    @ViewChildren('ssn') ssn: QueryList<SsnComponent>;
    @ViewChildren('address') address: QueryList<AddressComponent>;
    @ViewChildren('phone') phone: QueryList<PhoneNumberComponent>;
    @Input() authorizedUserRequired: boolean = false;
    @Input() coborrowerApplicant: ApplicantModel = new ApplicantModel();
    @Input() beneficiaryApplicants: ApplicantModel[] = [new ApplicantModel()];
    @Input('data-test-id') testId: string = 'beneficiary';
    @Output() event = new EventEmitter<boolean>();
    keyIdentifier: KeyIdentifierModel;
    appConfig: any = AppConfigService.config;
    beneficiaryType: any;
    survivorshipBeneficiary: boolean = false;
    minDobDate: Date = new Date();
    maxDobDate: Date = new Date();
    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';
    loading = false;
    percentageOfBenefit: number = 0;
    percentageError: boolean = false;
    maxBeneficiary: number =  this.appConfig.maxBeneficiary || 99;

    constructor(private sharedKeyDataService: SharedkeyDataService) {
        this.sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;
            });
        if (this.appConfig && this.appConfig.beneficiaryType) {
            this.beneficiaryType = Object.values(this.appConfig.beneficiaryType);
        } else {
            this.beneficiaryType = Object.values(['Child', 'Friend', 'Grandchild', 'Parent', 'Other', 'Sibling', 'Spouse']);
        }
        this.maxDobDate = moment().subtract(this.keyIdentifier.loanPurposeConfig?.benificaryMinAge || 0, 'years').toDate();
        this.minDobDate = moment().subtract(this.keyIdentifier.loanPurposeConfig?.benificaryMaxAge || 120, 'years').toDate();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.beneficiaryApplicants && changes.beneficiaryApplicants.currentValue) {
            this.beneficiaryApplicants = changes.beneficiaryApplicants.currentValue || [];
            if (this.beneficiaryApplicants.length) {
            this.beneficiaryApplicants.forEach((beneficiary: ApplicantModel) => {
                if (beneficiary?.percentageOwnership) {
                    this.percentageOfBenefit += Number(beneficiary.percentageOwnership);
                }
            });
        }
        }
    }

    updatePercentageOfBenefit() {
        this.percentageOfBenefit = 0;
        if (this.beneficiaryApplicants.length) {
            if (this.appConfig?.percentageOfBenefit && this.beneficiaryApplicants.length === this.maxBeneficiary) {
                // Auto-set last beneficiary's percentage to make total exactly 100
                const othersSum = this.beneficiaryApplicants
                    .slice(0, -1)
                    .reduce((sum, b) => sum + (Number(b.percentageOwnership) || 0), 0);
                this.beneficiaryApplicants[this.beneficiaryApplicants.length - 1].percentageOwnership = 100 - othersSum;
                this.percentageOfBenefit = 100;
            } else {
                this.beneficiaryApplicants.forEach((beneficiary: ApplicantModel) => {
                    if (beneficiary.percentageOwnership) {
                        this.percentageOfBenefit += Number(beneficiary.percentageOwnership);
                    }
                });
            }
        }
        if (this.appConfig?.percentageOfBenefit) {
            this.percentageError = this.beneficiaryApplicants.length > 0 && this.percentageOfBenefit !== 100;
        } else {
            this.percentageError = false;
        }
        this.event.emit(this.percentageError);
    }

    runValidation() {
        this._beneficiaryForm.form.markAllAsTouched();
        this.ssn.forEach(component => {
            component?.touch();
        });
        this.dob.forEach(component => {
            component?.touch();
        });
        this.address.forEach(component => {
            component?.touch();
        });
        this.phone.forEach(component => {
            component?.touch();
        });
    }

    addAdditionalBeneficiary() {
        this.runValidation();

        if (this._beneficiaryForm.form.invalid) {
              return;
        }
        const index = this.beneficiaryApplicants.length;
        const newBeneficiary = new ApplicantModel();
        if (this.appConfig?.percentageOfBenefit && index + 1 === this.maxBeneficiary) {
            newBeneficiary.percentageOwnership = 100 - this.percentageOfBenefit;
        }
        this.beneficiaryApplicants.push(newBeneficiary);

        /* call untouched after element render, so added timeout*/
        setTimeout(() => {
            this.markElementsUntouched(index);
            // Re-apply auto-populated percentage after reset clears the form control
            if (this.appConfig?.percentageOfBenefit && this.beneficiaryApplicants.length === this.maxBeneficiary) {
                const percentControl = this._beneficiaryForm.controls[`beneficiaryPercentage${index}`];
                const autoValue = this.beneficiaryApplicants[index]?.percentageOwnership;
                if (percentControl && autoValue !== undefined && autoValue !== null) {
                    percentControl.setValue(autoValue);
                }
            }
        }, 400);
    }

    markElementsUntouched(index: string | number) {
        Object.keys(this._beneficiaryForm.controls).forEach(fieldName => {
            if (fieldName.endsWith(String(index))) {
                this._beneficiaryForm.controls[fieldName].markAsPristine();
                // this._beneficiaryForm.controls[fieldName].setErrors(null);
                this._beneficiaryForm.controls[fieldName].markAsUntouched();
                this._beneficiaryForm.controls[fieldName].reset();
                // this._beneficiaryForm.controls[fieldName].updateValueAndValidity()
            }
        });
        // this.cdr.detectChanges();
    }

    removeAdditionalBeneficiary(index: number) {
        // remove data from additional array model
        this.beneficiaryApplicants.splice(index, 1);
        this._beneficiaryForm.form.updateValueAndValidity();
        this.updatePercentageOfBenefit();
    }

}
